import { ResizeNotifier } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';

/**
 * Event class for minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = { minimumReached };
  }
}

/**
 * Custom element that keeps all items visible (overflow removed)
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') this.#reset();
      else this.#reflowItems();
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.#waitForStyles();
    this.#initialize();
  }

  #waitForStyles() {
    const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');
    if (!styles || styles.sheet) return Promise.resolve();
    return new Promise((resolve) => styles.addEventListener('load', resolve));
  }

  #initialize() {
    const { shadowRoot } = this;
    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const list = shadowRoot.querySelector('[part="list"]');

    if (!(defaultSlot instanceof HTMLSlotElement) || !(list instanceof HTMLUListElement)) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    // Store references
    this.#refs = { defaultSlot, list };

    // Observe first and last elements if needed (optional)
    const elements = defaultSlot.assignedElements().filter(el => !(el instanceof HTMLTemplateElement));
    const firstElement = elements[0];
    const lastElement = elements[elements.length - 1];

    // IntersectionObserver for first/last elements
    if (firstElement) this.#intersectionObserver.observe(firstElement);
    if (lastElement && lastElement !== firstElement) this.#intersectionObserver.observe(lastElement);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  #scheduled = false;

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get defaultSlot() { return this.#refs.defaultSlot; }

  #handleIntersection = (entries) => {
    const entry = entries[0];
    if (entry?.isIntersecting) {
      this.#intersectionObserver.disconnect();
      setTimeout(() => {
        this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();
        this.#reflowItems(entry.boundingClientRect.height);
      }, 0);
    }
  };

  #handleChange = () => {
    if (this.#scheduled) return;
    this.#scheduled = true;
    requestAnimationFrame(() => setTimeout(() => {
      this.#reflowItems();
      this.#scheduled = false;
    }, 0));
  };

  #moveItemsToDefaultSlot() {
    const { defaultSlot } = this.#refs;
    for (const element of defaultSlot.assignedElements()) {
      element.slot = defaultSlot.name; // tous restent dans le slot principal
    }
  }

  #reset() {
    const { list } = this.#refs;
    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();
    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  showAll() {
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow simplified: all items stay visible, overflow logic removed
   */
  #reflowItems = (listHeight = 0) => {
    const { defaultSlot, list } = this.#refs;
    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Optional: adjust height if needed
    if (listHeight > 0) list.style.setProperty('height', `${listHeight}px`);

    list.style.setProperty('flex-wrap', 'wrap');
    list.style.setProperty('overflow', 'unset');

    this.style.setProperty('--overflow-count', `${elements.length}`);
    this.#observeChanges();
  };

  #observeChanges() {
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #refs;

  #resizeObserver = new ResizeNotifier(this.#handleChange);
  #mutationObserver = new MutationObserver(this.#handleChange);
  #intersectionObserver = new IntersectionObserver(this.#handleIntersection);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}